import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  CheckboxListCard,
  CheckboxListItem,
  Highlight,
  IconBannersWarning,
  HStack,
  Text,
  VStack,
  ScrollView,
} from '@adc/parallax-component-library';
import { Dialog } from 'tamagui';
import { Account, AccountWithRoleAssignments, Role } from '../../models';
import i18n from '../../utilities/i18n';
import SearchableDropdown, {
  SearchableDropdownItem,
} from '../searchableDropdown/SearchableDropdown';
import RoleAssignmentsConfirmationModal from './RoleAssignmentsConfirmationModal';
import './Modal.scss';

interface RoleAssignmentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedAccount?: AccountWithRoleAssignments | null;
  accountsWithRoleAssignments?: AccountWithRoleAssignments[];
  availableRoles: Role[];
  isSaving?: boolean;
  onSave: (
    account: AccountWithRoleAssignments,
    addRoleKeys: string[],
    removeRoleKeys: string[],
  ) => Promise<void>;
  allAccounts?: Account[];
}

const getAccountIdentity = (account: Account) => {
  return `${account.firstName} ${account.lastName} (${account.email})`;
};

const getRoleKeys = (account: AccountWithRoleAssignments | null) => {
  if (!account) {
    return [];
  }

  return account.roleAssignments.map(
    (roleAssignment) => roleAssignment.role.key,
  );
};

/**
 * Modal for viewing and editing role assignments for a selected account
 * @param {boolean} isOpen - Whether the modal is open
 * @param {() => void} onClose - Callback when modal closes
 * @param {AccountWithRoleAssignments} selectedAccount - Pre-selected account for edit mode; omit for add mode
 * @param {AccountWithRoleAssignments[]} accountsWithRoleAssignments - Currently assigned accounts, used to filter the add-mode dropdown
 * @param {Role[]} availableRoles - Available roles to assign
 * @param {Account[]} allAccounts - All accounts available for selection in add mode
 * @param {(account: AccountWithRoleAssignments, addRoleKeys: string[], removeRoleKeys: string[]) => Promise<void>} onSave - Callback when saving role changes
 * @returns {React.JSX.Element}
 */
export default function RoleAssignmentsModal({
  isOpen,
  onClose,
  allAccounts = [],
  accountsWithRoleAssignments = [],
  selectedAccount,
  availableRoles,
  isSaving = false,
  onSave,
}: RoleAssignmentsModalProps) {
  const isEditMode = !!selectedAccount;

  const [selectedUser, setSelectedUser] =
    useState<AccountWithRoleAssignments | null>(selectedAccount || null);

  const originalRoleKeys = getRoleKeys(selectedUser);

  const [selectedRoleKeys, setSelectedRoleKeys] =
    useState<string[]>(originalRoleKeys);

  const addRoleKeys = selectedRoleKeys.filter(
    (roleKey) => !originalRoleKeys.includes(roleKey),
  );
  const removeRoleKeys = originalRoleKeys.filter(
    (roleKey) => !selectedRoleKeys.includes(roleKey),
  );

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [hasSaveError, setHasSaveError] = useState(false);

  const handleUserChange = (item: SearchableDropdownItem) => {
    const selectedOption = allAccounts.find(
      (account) => account.key === item.key,
    );

    if (selectedOption) {
      selectAccount({
        ...selectedOption,
        roleAssignments: [],
      });
    }
  };

  const handleRoleToggle = (roleKey: string) => {
    setSelectedRoleKeys((prevSelectedRoleKeys) => {
      if (prevSelectedRoleKeys.includes(roleKey)) {
        return prevSelectedRoleKeys.filter((key) => key !== roleKey);
      }

      return [...prevSelectedRoleKeys, roleKey];
    });
  };

  const handleSave = () => {
    if (isSaving) {
      return;
    }

    setHasSaveError(false);
    setShowConfirmation(true);
  };

  const selectAccount = (account: AccountWithRoleAssignments | null) => {
    setSelectedUser(account);
    setSelectedRoleKeys(getRoleKeys(account));
  };

  const dropdownOptions = useMemo(() => {
    return allAccounts
      .filter(
        (user) =>
          !accountsWithRoleAssignments.some(
            (account) => account.key === user.key,
          ),
      )
      .slice()
      .sort((a, b) =>
        getAccountIdentity(a).localeCompare(getAccountIdentity(b)),
      )
      .map((user) => ({
        key: user.key,
        label: getAccountIdentity(user),
        user,
      }));
  }, [allAccounts, accountsWithRoleAssignments]);

  const renderUserSelector = () => {
    if (isEditMode) {
      return (
        <Text type="$bodyBase.default">
          {getAccountIdentity(selectedAccount)}
        </Text>
      );
    }

    return (
      <SearchableDropdown
        items={dropdownOptions.map((option) => ({
          key: option.key,
          value: option.label,
        }))}
        selectedItem={
          selectedUser
            ? {
                key: selectedUser.key,
                value: getAccountIdentity(selectedUser),
              }
            : undefined
        }
        onChange={handleUserChange}
        disabled={isSaving}
      />
    );
  };

  const onConfirmSave = async () => {
    if (isSaving) {
      return;
    }

    if (!selectedUser) {
      setShowConfirmation(false);
      return;
    }

    try {
      await onSave(selectedUser, addRoleKeys, removeRoleKeys);
      setHasSaveError(false);
      setShowConfirmation(false);
      onClose();
    } catch {
      setShowConfirmation(false);
      setHasSaveError(true);
    }
  };

  // sync state when modal opens
  useEffect(() => {
    if (isOpen) {
      selectAccount(selectedAccount || null);
      setShowConfirmation(false);
      setHasSaveError(false);
    }
  }, [isOpen, selectedAccount]);

  return (
    <>
      <Dialog open={isOpen}>
        {/* Need to specify zIndex values so DropdownList popup renders above Dialog consistently */}
        <Dialog.Portal zIndex={1000}>
          <Dialog.Overlay key="overlay" zIndex={1000} />
          <Dialog.Content
            key="content"
            width="75%"
            maxHeight="90%"
            zIndex={1001}
            overflow="visible"
          >
            <VStack gap="$2">
              <Dialog.Title>
                <Text type="$h3.strong">
                  {i18n.t('Modal_RoleAssignments.title')}
                </Text>
              </Dialog.Title>
            </VStack>

            <VStack gap="$2" paddingVertical="$4" flex={1}>
              {hasSaveError ? (
                <Highlight
                  variant="danger"
                  iconStart={
                    <IconBannersWarning
                      color="$neutral.0"
                      accentColor="$support.danger.default"
                    />
                  }
                  title={i18n.t('Modal_RoleAssignments.error_header')}
                  body={i18n.t('Modal_RoleAssignments.error_generic')}
                  testID="account-assignments-save-error-banner"
                />
              ) : null}

              <VStack gap="$2" paddingBottom="$4">
                <Text type="$bodyBase.strong">
                  {i18n.t('Modal_RoleAssignments.select_user_title')}
                </Text>
                {renderUserSelector()}
              </VStack>

              <VStack gap="$2" flex={1} minHeight={0}>
                <Text type="$bodyBase.strong">
                  {i18n.t('Modal_RoleAssignments.roles')}
                </Text>
                <ScrollView flex={1} minHeight={0}>
                  <CheckboxListCard separated>
                    {availableRoles.map((role) => {
                      return (
                        <CheckboxListItem
                          key={role.key}
                          title={role.name}
                          label={role.description}
                          checked={selectedRoleKeys.includes(role.key)}
                          onPress={() => handleRoleToggle(role.key)}
                          testID={`role-checkbox-${role.key}`}
                          aria-label={`${role.key} - ${role.description}`}
                          disabled={isSaving || !selectedUser}
                        />
                      );
                    })}
                  </CheckboxListCard>
                </ScrollView>
              </VStack>
            </VStack>

            <HStack gap="$2" justifyContent="flex-end">
              <Button
                variant="secondary"
                size="medium"
                label={i18n.t('Common.cancel')}
                onPress={onClose}
                disabled={isSaving}
                testID="account-assignments-cancel-button"
              />
              <Button
                variant="primary"
                size="medium"
                label={i18n.t('Common.save')}
                onPress={handleSave}
                disabled={
                  isSaving ||
                  !selectedUser ||
                  (addRoleKeys.length === 0 && removeRoleKeys.length === 0)
                }
                testID="account-assignments-save-button"
              />
            </HStack>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>

      {selectedUser ? (
        <RoleAssignmentsConfirmationModal
          isOpen={showConfirmation}
          onClose={() => setShowConfirmation(false)}
          onConfirm={onConfirmSave}
          selectedAccount={selectedUser}
          availableRoles={availableRoles}
          selectedRoleKeys={selectedRoleKeys}
          addRoleKeys={addRoleKeys}
          removeRoleKeys={removeRoleKeys}
          isSaving={isSaving}
        />
      ) : null}
    </>
  );
}
